import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { message } from "antd";
import formListService from "../services/formListService";

/**
 * FormList bileşeni için state yönetimi hook'u
 */
export const useFormListLogic = () => {
  // Redux state
  const appToken = useSelector((state) => state.appToken.apptoken);
  const userData = useSelector((state) => state.loginData.loginData);

  // Form listesi state'leri
  const [forms, setForms] = useState([]);
  const [filteredForms, setFilteredForms] = useState([]);
  const [loading, setLoading] = useState(true);

  // Arama ve filtreleme state'leri
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");

  // Dialog state'leri
  const [shareDialog, setShareDialog] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyFormTitle, setCopyFormTitle] = useState("");
  const [selectedFormForCopy, setSelectedFormForCopy] = useState(null);
  const [publicShareDialog, setPublicShareDialog] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState("");

  // Menu state'leri
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedFormForMenu, setSelectedFormForMenu] = useState(null);

  // Loading state'leri
  const [copyLoading, setCopyLoading] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);

  // Seçim state'leri
  const [selectedForms, setSelectedForms] = useState([]);

  // Formları yükle
  useEffect(() => {
    fetchForms();
  }, [userData, appToken]);

  // Filtreleme ve sıralama
  useEffect(() => {
    filterAndSortForms();
  }, [forms, searchTerm, statusFilter, sortOrder]);

  /**
   * Formları API'den getirir
   */
  const fetchForms = async () => {
    if (!appToken || !userData) return;

    setLoading(true);
    try {
      const formsData = await formListService.fetchForms(appToken, userData.id);
      setForms(formsData);
      setFilteredForms(formsData);
    } catch (error) {
      console.error("Fetch forms error:", error);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Formları filtreler ve sıralar
   */
  const filterAndSortForms = () => {
    let result = [...forms];

    // Arama filtresi
    if (searchTerm) {
      result = result.filter((form) =>
        (form.title || "İsimsiz Form")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    // Durum filtresi
    if (statusFilter !== "all") {
      result = result.filter((form) =>
        statusFilter === "active" ? form.isActive : !form.isActive
      );
    }

    // Sıralama
    result.sort((a, b) => {
      const dateA = new Date(a.createDate || 0);
      const dateB = new Date(b.createDate || 0);

      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });

    setFilteredForms(result);
  };

  /**
   * Form durumunu değiştirir
   */
  const toggleFormStatus = async (id, currentStatus) => {
    const hide = message.loading("Durum güncelleniyor...", 0);
    try {
      await formListService.toggleFormStatus(
        id,
        !currentStatus,
        appToken,
        userData.id
      );

      const updatedForms = forms.map((form) =>
        form.id === id ? { ...form, isActive: !currentStatus } : form
      );

      setForms(updatedForms);
      hide();
      message.success(
        `Form ${!currentStatus ? "aktif" : "pasif"} duruma getirildi`
      );
      fetchForms(); // Verileri yenile
    } catch (error) {
      hide();
      message.error(error.message);
    }
  };

  /**
   * Form siler
   */
  const deleteForm = async (formId) => {
    const hide = message.loading("Form siliniyor...", 0);
    try {
      await formListService.deleteForm(formId, appToken, userData.id);
      setForms(forms.filter((form) => form.id !== formId));
      hide();
      message.success("Form başarıyla silindi");
    } catch (error) {
      hide();
      message.error(error.message);
    }
  };

  /**
   * Form kopyalar
   */
  const copyForm = async (form) => {
    const hide = message.loading("Form çoğaltılıyor...", 0);
    setCopyLoading(true);
    try {
      await formListService.copyForm(form, appToken, userData.id);
      hide();
      message.success("Form başarıyla çoğaltıldı");
      fetchForms(); // Listeyi yenile
    } catch (error) {
      hide();
      message.error(error.message);
    } finally {
      setCopyLoading(false);
    }
  };

  /**
   * Formu arşivler
   */
  const archiveForm = async (formId) => {
    const hide = message.loading("Form arşivleniyor...", 0);
    setArchiveLoading(true);
    try {
      await formListService.archiveForm(formId, appToken, userData.id);
      setForms((prevForms) => prevForms.filter((form) => form.id !== formId));
      hide();
      message.success("Form başarıyla arşivlendi");
    } catch (error) {
      hide();
      message.error(error.message);
    } finally {
      setArchiveLoading(false);
    }
  };

  /**
   * Public token oluşturur
   */
  const generatePublicToken = async (form) => {
    try {
      const hide = message.loading(
        "Public görüntüleme linki oluşturuluyor...",
        0
      );

      const publicUrl = formListService.generatePublicFormUrl(form.formId);

      navigator.clipboard.writeText(publicUrl);

      setSelectedForm({ ...form, publicUrl });
      setPublicShareDialog(true);

      hide();
      message.success(
        "Public görüntüleme linki oluşturuldu ve panoya kopyalandı!"
      );
    } catch (error) {
      console.error("Public token oluşturma hatası:", error);
      message.error("Public link oluşturulurken hata oluştu");
    }
  };

  /**
   * Spreadsheet token oluşturur
   */
  const generateSpreadsheetToken = async (form) => {
    try {
      const hide = message.loading(
        "Tablo görüntüleme linki oluşturuluyor...",
        0
      );

      const publicUrl = formListService.generatePublicSpreadsheetUrl(
        form.formId
      );

      navigator.clipboard.writeText(publicUrl);

      setSelectedForm({ ...form, publicUrl, viewType: "spreadsheet" });
      setPublicShareDialog(true);

      hide();
      message.success(
        "Tablo görüntüleme linki oluşturuldu ve panoya kopyalandı!"
      );
    } catch (error) {
      console.error("Spreadsheet token oluşturma hatası:", error);
      message.error("Tablo linki oluşturulurken hata oluştu");
    }
  };

  /**
   * Form paylaşımı
   */
  const shareForm = (formId) => {
    const key = "updatable";
    message.loading({
      content: "Form bağlantısına yönlendiriliyorsunuz...",
      key,
    });
    setTimeout(() => {
      const shareUrl = formListService.generateShareUrl(formId);
      window.open(shareUrl, "_blank");
      message.success({
        content: "Başarıyla yönlendirildiniz!",
        key,
        duration: 2,
      });
    }, 1000);
  };

  /**
   * Menu işlemleri
   */
  const handleMenuClick = (event, form) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedFormForMenu(form);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedFormForMenu(null);
  };

  /**
   * Dialog işlemleri
   */
  const handleOpenCopyDialog = (form) => {
    setSelectedFormForCopy(form);
    setCopyFormTitle(`${form.title || "İsimsiz Form"} (Kopya)`);
    setCopyDialogOpen(true);
  };

  const handleCloseCopyDialog = () => {
    setCopyDialogOpen(false);
    setSelectedFormForCopy(null);
    setCopyFormTitle("");
  };

  /**
   * Form seçimi işlemleri
   */
  const handleFormSelect = (formId) => {
    setSelectedForms((prev) => {
      if (prev.includes(formId)) {
        return prev.filter((id) => id !== formId);
      } else {
        return [...prev, formId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedForms.length === filteredForms.length) {
      setSelectedForms([]);
    } else {
      setSelectedForms(filteredForms.map((form) => form.id));
    }
  };

  /**
   * Export işlemleri
   */
  const handleExportClick = (type) => {
    setExportType(type);
    setExportDialogOpen(true);
  };

  return {
    // State
    forms,
    filteredForms,
    loading,
    searchTerm,
    statusFilter,
    sortOrder,
    selectedForm,
    shareDialog,
    copyDialogOpen,
    copyFormTitle,
    selectedFormForCopy,
    publicShareDialog,
    exportDialogOpen,
    exportType,
    menuAnchorEl,
    selectedFormForMenu,
    copyLoading,
    archiveLoading,
    selectedForms,

    // Setters
    setSearchTerm,
    setStatusFilter,
    setSortOrder,
    setShareDialog,
    setPublicShareDialog,
    setExportDialogOpen,

    // Actions
    fetchForms,
    toggleFormStatus,
    deleteForm,
    copyForm,
    archiveForm,
    generatePublicToken,
    generateSpreadsheetToken,
    shareForm,
    handleMenuClick,
    handleMenuClose,
    handleOpenCopyDialog,
    handleCloseCopyDialog,
    handleFormSelect,
    handleSelectAll,
    handleExportClick,

    // Utils
    appToken,
    userData,
  };
};
